import { useState, useEffect } from "react";
import { Package, Loader2, Mail, Clock, CheckCircle, Truck, Box, CreditCard, XCircle, DollarSign, MapPin } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

interface OrderItem {
  id: string;
  product_name: string;
  variant_info: string | null;
  cover_image: string | null;
  quantity: number;
  unit_price: number;
}

interface Order {
  id: string;
  total_price: number;
  status: string;
  created_at: string;
  notes: string | null;
  shipping_address_id: string | null;
  payment_method_id: string | null;
  items: OrderItem[];
}

const statusLabels: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
  "pending": { label: "In attesa di conferma", icon: <Clock className="w-4 h-4" />, color: "text-amber-500 bg-amber-500/10" },
  "confirmed": { label: "Confermato - da pagare", icon: <DollarSign className="w-4 h-4" />, color: "text-blue-500 bg-blue-500/10" },
  "cancelled": { label: "Annullato", icon: <XCircle className="w-4 h-4" />, color: "text-destructive bg-destructive/10" },
  "paid": { label: "Pagato", icon: <CheckCircle className="w-4 h-4" />, color: "text-green-500 bg-green-500/10" },
  "preparing": { label: "In preparazione", icon: <Truck className="w-4 h-4" />, color: "text-purple-500 bg-purple-500/10" },
  "packaging": { label: "Imballaggio", icon: <Box className="w-4 h-4" />, color: "text-yellow-500 bg-yellow-500/10" },
  "ready_pickup": { label: "Pronto da ritirare", icon: <CheckCircle className="w-4 h-4" />, color: "text-green-500 bg-green-500/10" },
  "ready_delivery": { label: "Pronto per la consegna", icon: <Truck className="w-4 h-4" />, color: "text-green-500 bg-green-500/10" },
  "picked_up": { label: "Ritirato", icon: <Box className="w-4 h-4" />, color: "text-violet-500 bg-violet-500/10" },
  "delivered": { label: "Consegnato", icon: <CheckCircle className="w-4 h-4" />, color: "text-violet-500 bg-violet-500/10" },
};

export const UserOrders = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [payingOrderId, setPayingOrderId] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchOrders();
    
    const channel = supabase
      .channel('user-orders')
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'orders' },
        (payload) => {
          setOrders(prev => prev.map(order => 
            order.id === payload.new.id 
              ? { ...order, status: payload.new.status as string }
              : order
          ));
          // Show toast for status changes
          const newStatus = statusLabels[payload.new.status as string];
          if (newStatus) {
            toast.info(`Ordine aggiornato: ${newStatus.label}`);
          }
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, []);

  const fetchOrders = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: ordersData, error } = await supabase
        .from('orders')
        .select('id, total_price, status, created_at, notes, shipping_address_id, payment_method_id')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Fetch order items for all orders
      const orderIds = (ordersData || []).map(o => o.id);
      const { data: itemsData } = await supabase
        .from('order_items')
        .select('id, order_id, product_name, variant_info, cover_image, quantity, unit_price')
        .in('order_id', orderIds);

      const itemsByOrder = new Map<string, OrderItem[]>();
      (itemsData || []).forEach(item => {
        const existing = itemsByOrder.get(item.order_id) || [];
        existing.push(item);
        itemsByOrder.set(item.order_id, existing);
      });

      const ordersWithItems: Order[] = (ordersData || []).map(order => ({
        ...order,
        items: itemsByOrder.get(order.id) || [],
      }));

      setOrders(ordersWithItems);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Errore nel caricamento degli ordini');
    } finally {
      setLoading(false);
    }
  };

  const handlePayOrder = async (orderId: string) => {
    setPayingOrderId(orderId);
    try {
      // Navigate to a payment selection flow
      // For now, mark as paid directly (can be enhanced with payment method selection)
      const { error } = await supabase
        .from('orders')
        .update({ status: 'paid' })
        .eq('id', orderId);

      if (error) throw error;
      
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: 'paid' } : o));
      toast.success("Pagamento completato!");
    } catch (error) {
      console.error('Error paying order:', error);
      toast.error("Errore nel pagamento");
    } finally {
      setPayingOrderId(null);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {orders.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Package className="w-12 h-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground font-medium">Al momento non ci sono ordini effettuati</p>
            <p className="text-sm text-muted-foreground/70 mt-1">I tuoi ordini appariranno qui</p>
          </div>
        ) : (
          orders.map((order) => {
            const statusInfo = statusLabels[order.status] || statusLabels.pending;
            
            return (
              <div key={order.id} className="p-4 rounded-xl bg-card/80 border border-border/50 space-y-3">
                {/* Order items */}
                {order.items.map(item => (
                  <div key={item.id} className="flex items-start gap-3">
                    {item.cover_image && (
                      <img src={item.cover_image} alt={item.product_name} className="w-14 h-14 rounded-lg object-cover" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-foreground truncate">{item.product_name}</p>
                      {item.variant_info && <p className="text-xs text-muted-foreground">{item.variant_info}</p>}
                      <p className="text-sm text-muted-foreground">
                        Qtà: {item.quantity} • €{(item.unit_price * item.quantity).toFixed(2)}
                      </p>
                    </div>
                  </div>
                ))}

                {order.items.length > 1 && (
                  <div className="flex justify-between pt-2 border-t border-border/30 text-sm">
                    <span className="text-muted-foreground">Totale ordine</span>
                    <span className="font-bold">€{order.total_price.toFixed(2)}</span>
                  </div>
                )}

                <div className="flex items-center justify-between pt-2 border-t border-border/30">
                  <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${statusInfo.color}`}>
                    {statusInfo.icon}
                    <span>{statusInfo.label}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">{formatDate(order.created_at)}</p>
                </div>

                {/* Pay button for confirmed orders */}
                {order.status === 'confirmed' && (
                  <Button
                    className="w-full gap-2 rounded-xl"
                    onClick={() => handlePayOrder(order.id)}
                    disabled={payingOrderId === order.id}
                  >
                    <CreditCard className="w-4 h-4" />
                    {payingOrderId === order.id ? "Elaborazione..." : "Paga ora"}
                  </Button>
                )}
              </div>
            );
          })
        )}
      </div>
      
      <div className="p-4 border-t border-border/50">
        <a
          href="mailto:assistenza.app26@gmail.com"
          className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-primary text-primary-foreground font-medium rounded-xl transition-all duration-300 hover:bg-primary/90 hover:scale-[1.02] active:scale-[0.98]"
        >
          <Mail className="w-5 h-5" />
          <span>Contattaci</span>
        </a>
      </div>
    </div>
  );
};

export default UserOrders;
